import { useState } from 'react'
import {
  ShieldCheck,
  Clock,
  AlertTriangle,
  RefreshCw,
  Filter,
} from 'lucide-react'
import {
  usePendingAttestations,
  useAttestationQueueStats,
  useApproveAttestation,
  useRejectAttestation,
  AdminAttestation,
} from '@/hooks/useAdminAttestations'
import AttestationReviewCard from '@/components/employee/AttestationReviewCard'

// Framework display info
const FRAMEWORK_INFO: Record<string, { name: string; product: string; color: string }> = {
  hipaa: { name: 'HIPAA', product: 'Clinical', color: 'text-red-600 bg-red-100' },
  eeoc: { name: 'EEOC', product: 'Talent', color: 'text-blue-600 bg-blue-100' },
  batson: { name: 'Batson', product: 'Legal', color: 'text-purple-600 bg-purple-100' },
  ferpa: { name: 'FERPA', product: 'Education', color: 'text-green-600 bg-green-100' },
  finra: { name: 'FINRA', product: 'Finance', color: 'text-amber-600 bg-amber-100' },
}

type FrameworkFilter = 'all' | 'hipaa' | 'eeoc' | 'batson' | 'ferpa' | 'finra'
type SortOption = 'submitted_at' | 'framework' | 'tenant_name'

export default function AttestationQueue() {
  const [frameworkFilter, setFrameworkFilter] = useState<FrameworkFilter>('all')
  const [sortBy, setSortBy] = useState<SortOption>('submitted_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [actionInProgress, setActionInProgress] = useState<string | null>(null)

  const { data, isLoading, error, refetch } = usePendingAttestations({
    framework: frameworkFilter === 'all' ? undefined : frameworkFilter,
    sortBy,
    sortOrder,
  })

  const { data: stats } = useAttestationQueueStats()

  const approveMutation = useApproveAttestation()
  const rejectMutation = useRejectAttestation()

  const handleQuickApprove = async (attestation: AdminAttestation) => {
    if (!confirm(`Approve ${FRAMEWORK_INFO[attestation.framework]?.name || attestation.framework} attestation for ${attestation.tenant_name}?`)) {
      return
    }

    setActionInProgress(attestation.id)
    try {
      await approveMutation.mutateAsync({ id: attestation.id })
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to approve attestation')
    } finally {
      setActionInProgress(null)
    }
  }

  const handleQuickReject = async (attestation: AdminAttestation) => {
    const reason = prompt('Enter rejection reason:')
    if (!reason) return

    setActionInProgress(attestation.id)
    try {
      await rejectMutation.mutateAsync({ id: attestation.id, notes: reason })
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to reject attestation')
    } finally {
      setActionInProgress(null)
    }
  }

  const frameworkTabs: { key: FrameworkFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'hipaa', label: 'HIPAA' },
    { key: 'eeoc', label: 'EEOC' },
    { key: 'batson', label: 'Batson' },
    { key: 'ferpa', label: 'FERPA' },
    { key: 'finra', label: 'FINRA' },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attestation Review Queue</h1>
          <p className="text-gray-600 mt-1">Review and approve tenant compliance attestations</p>
        </div>
        <button
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <StatCard
          label="Total Pending"
          value={stats?.total_pending || 0}
          icon={<Clock className="w-5 h-5 text-blue-600" />}
          bgColor="bg-blue-50"
        />
        <StatCard
          label="Priority (>3 days)"
          value={stats?.priority_count || 0}
          icon={<AlertTriangle className="w-5 h-5 text-amber-600" />}
          bgColor="bg-amber-50"
          isWarning={(stats?.priority_count || 0) > 0}
        />
        <StatCard
          label="Oldest Pending"
          value={`${stats?.oldest_pending_days || 0} days`}
          icon={<Clock className="w-5 h-5 text-gray-600" />}
          bgColor="bg-gray-50"
          isWarning={(stats?.oldest_pending_days || 0) > 3}
        />
        <StatCard
          label="Frameworks"
          value={Object.keys(stats?.by_framework || {}).length}
          icon={<ShieldCheck className="w-5 h-5 text-personic-600" />}
          bgColor="bg-personic-50"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        {/* Framework Tabs */}
        <div className="flex flex-wrap gap-2">
          {frameworkTabs.map((tab) => {
            const count = tab.key === 'all'
              ? stats?.total_pending || 0
              : stats?.by_framework?.[tab.key] || 0
            return (
              <button
                key={tab.key}
                onClick={() => setFrameworkFilter(tab.key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  frameworkFilter === tab.key
                    ? 'bg-personic-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tab.label}
                {count > 0 && (
                  <span className="ml-1.5 text-xs opacity-75">({count})</span>
                )}
              </button>
            )
          })}
        </div>

        {/* Sort Options */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-personic-500"
          >
            <option value="submitted_at">Submitted Date</option>
            <option value="framework">Framework</option>
            <option value="tenant_name">Tenant Name</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
            title={sortOrder === 'asc' ? 'Oldest first' : 'Newest first'}
          >
            {sortOrder === 'asc' ? '(Oldest)' : '(Newest)'}
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">Failed to load attestations. Please try again.</p>
          <button
            onClick={() => refetch()}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && data?.attestations.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
          <ShieldCheck className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Queue is Empty
          </h3>
          <p className="text-gray-500">
            {frameworkFilter === 'all'
              ? 'No pending attestations to review. Great job!'
              : `No pending ${FRAMEWORK_INFO[frameworkFilter]?.name} attestations.`}
          </p>
        </div>
      )}

      {/* Attestation List */}
      {!isLoading && !error && data && data.attestations.length > 0 && (
        <div className="space-y-4">
          {data.attestations.map((attestation) => (
            <AttestationReviewCard
              key={attestation.id}
              attestation={attestation}
              frameworkInfo={FRAMEWORK_INFO[attestation.framework]}
              isLoading={actionInProgress === attestation.id}
              onApprove={() => handleQuickApprove(attestation)}
              onReject={() => handleQuickReject(attestation)}
            />
          ))}
        </div>
      )}

      {/* Results Count */}
      {data && (
        <p className="text-sm text-gray-500">
          Showing {data.attestations.length} of {data.total_pending} pending attestations
        </p>
      )}
    </div>
  )
}

function StatCard({
  label,
  value,
  icon,
  bgColor,
  isWarning = false,
}: {
  label: string
  value: string | number
  icon: React.ReactNode
  bgColor: string
  isWarning?: boolean
}) {
  return (
    <div className={`p-4 rounded-xl border ${isWarning ? 'border-amber-300' : 'border-gray-200'} ${bgColor}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">{label}</p>
          <p className={`text-2xl font-bold mt-1 ${isWarning ? 'text-amber-600' : 'text-gray-900'}`}>
            {value}
          </p>
        </div>
        <div className={`p-2 rounded-lg ${isWarning ? 'bg-amber-100' : 'bg-white'}`}>
          {icon}
        </div>
      </div>
    </div>
  )
}
